import { mapExtrasToBasket } from '@common/features/RoomRateConfigurationWizard/FormSteps/ExtrasStep/utils';
import { getGuestGenderByTitle, parseDateToString } from '@utils/helpers';
import { some } from '@utils/some';
import { required } from '@utils/validators';
import Cookie from 'js-cookie';
import { Guest } from 'shared-types/Basket';

export const roomRateParseForBasket =
  (updateBasket, createBasket) =>
  (dispatch) =>
  ({ basketDetails, journeyParams, property, language, entity, metaEntity }) =>
  ({ guestDetails = [], note = '', extras = [] }) => {
    const basketId = Cookie.get('basketId');

    const metaEntityId = metaEntity.id;
    const entityId = entity.id;

    // remove optional empty and half empty guests
    const filteredGuestDetails = (guestDetails as Guest[])
      .filter(
        (guest) => !required(guest.firstName) && !required(guest.lastName) && !required(guest.title)
      )
      .map(({ title, firstName, lastName, gender, externalSpaId, loggedInUser }) => ({
        title,
        firstName,
        lastName,
        gender: gender || getGuestGenderByTitle(title),
        externalSpaId,
        loggedInUser,
      }));

    const { promoRateCode = null } = journeyParams;

    const data = {
      quantity: 1,
      type: 'rooms',
      checkIn: parseDateToString(journeyParams.fromDate),
      checkOut: parseDateToString(journeyParams.toDate),
      adults: journeyParams.adults || 0,
      children: journeyParams.children || 0,
      guestDetails: filteredGuestDetails,
      ...(note && { note }),
      bookable: {
        ids: {
          rateId: metaEntity.type === 'rate' ? metaEntityId : entityId,
          roomId: metaEntity.type === 'room' ? metaEntityId : entityId,
          promoRateCode,
        },
        ...(extras.length && { roomExtras: extras }),
      },
    };

    if (journeyParams.childAges && Object.values(journeyParams.childAges).length) {
      data.bookable['roomsChildAges'] = Object.values(journeyParams.childAges);
    }

    if (basketId) {
      const items = [...(basketDetails?.items || []), data];
      return dispatch(
        updateBasket(property.id, basketId, language, { items }, property.slug, property.timezone)
      );
    }
    return dispatch(
      createBasket(property.id, language, { items: [data] }, property.slug, property.timezone)
    );
  };

type MultiRoomGuest = Guest & {
  room: number;
};
export const multiRoomRateParseForBasket =
  (updateBasket, createBasket) =>
  (dispatch) =>
  ({ basketDetails, journeyParams, property, language, entity, metaEntity }) =>
  ({
    guestDetails = [],
    note = '',
    extras = [],
  }: {
    guestDetails: MultiRoomGuest[];
    note: string;
    extras: ReturnType<typeof mapExtrasToBasket>[];
  }) => {
    const basketId = Cookie.get('basketId');
    const metaEntityId = metaEntity.id;
    const entityId = entity.id;
    const bookableRooms = [] as any[];
    journeyParams.rooms.forEach((room, i) => {
      const guests = guestDetails.filter((guest) => guest.room === i + 1);
      const parsedExtras = extras[i];
      const filteredGuestDetails = (guests as Guest[])
        .filter(
          (guest) =>
            !required(guest.firstName) && !required(guest.lastName) && !required(guest.title)
        )
        .map(({ title, firstName, lastName, gender, externalSpaId, loggedInUser }) => ({
          title,
          firstName,
          lastName,
          gender: gender || getGuestGenderByTitle(title),
          externalSpaId,
          loggedInUser,
        }));

      const { promoRateCode = null } = journeyParams;

      const data = {
        quantity: 1,
        type: 'rooms',
        checkIn: parseDateToString(journeyParams.fromDate),
        checkOut: parseDateToString(journeyParams.toDate),
        adults: room.adults || 0,
        children: room.children || 0,
        guestDetails: filteredGuestDetails,
        ...(note && { note }),
        bookable: {
          ids: {
            rateId: metaEntity.type === 'rate' ? metaEntityId : entityId,
            roomId: metaEntity.type === 'room' ? metaEntityId : entityId,
            promoRateCode,
          },
          ...(some(parsedExtras) && { roomExtras: parsedExtras }),
        },
      };

      if (journeyParams.childAges && Object.values(journeyParams.childAges).length) {
        data.bookable['roomsChildAges'] = Object.values(journeyParams.childAges);
      }

      bookableRooms.push(data);
    });

    if (basketId) {
      const items = [...(basketDetails?.items || []), ...bookableRooms];
      return dispatch(
        updateBasket(property.id, basketId, language, { items }, property.slug, property.timezone)
      );
    }
    return dispatch(
      createBasket(
        property.id,
        language,
        { items: bookableRooms },
        property.slug,
        property.timezone
      )
    );
  };
